import type {
  OdooSearchItem,
  OdooPartnerItem,
  OdooProductItem,
} from '@/types/odoo.types';
import { isOdooProductItem, isOdooPartnerItem } from '@/types/odoo.types';

// ---------------------------------------------------------------------------
// Country lookup helper
// ---------------------------------------------------------------------------

interface CountryRef {
  _id: string;
  name: string;
}

/**
 * Attempt to match the Odoo `country_id[1]` name string to a Hub country `_id`.
 * Returns the `_id` if found, otherwise `null`.
 */
export function matchCountryByName(
  odooCountryName: string | undefined,
  countries: CountryRef[]
): string | null {
  if (!odooCountryName) return null;
  const normalised = odooCountryName.trim().toLowerCase();
  const match = countries.find((c) => c.name.trim().toLowerCase() === normalised);
  return match?._id ?? null;
}

/**
 * Extract the country name from an Odoo partner's `country_id` field.
 * Odoo returns `[id, name]` or `false`.
 */
function extractCountryName(partner: OdooPartnerItem): string | undefined {
  if (Array.isArray(partner.country_id)) {
    return partner.country_id[1];
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Pre-fill result types
// ---------------------------------------------------------------------------

export interface OrganizationPreFill {
  name?: string;
  address?: string;
  /** Hub country _id if matched, otherwise undefined. */
  country?: string;
  /** Original Odoo country name when no Hub match was found. */
  unmatchedCountry?: string;
}

export interface LegalEntityPreFill {
  legal_name?: string;
  tax_number?: string;
  address?: string;
  country?: string;
  unmatchedCountry?: string;
}

export interface ContactPreFill {
  firstName?: string;
  lastName?: string;
  email?: string;
  country?: string;
  unmatchedCountry?: string;
}

export interface PlanPreFill {
  name?: string;
  listPrice?: number;
  listCost?: number;
}

// ---------------------------------------------------------------------------
// Mapper functions
// ---------------------------------------------------------------------------

/**
 * Map an Odoo partner item to Organization form pre-fill values.
 */
export function mapOdooToOrganization(
  item: OdooSearchItem,
  countries: CountryRef[]
): OrganizationPreFill {
  if (!isOdooPartnerItem(item)) return {};
  const result: OrganizationPreFill = {};

  if (item.name) result.name = item.name;
  if (typeof item.street === 'string' && item.street) result.address = item.street;

  const countryName = extractCountryName(item);
  const countryId = matchCountryByName(countryName, countries);
  if (countryId) {
    result.country = countryId;
  } else if (countryName) {
    result.unmatchedCountry = countryName;
  }

  return result;
}

/**
 * Map an Odoo partner item to Legal Entity form pre-fill values.
 */
export function mapOdooToLegalEntity(
  item: OdooSearchItem,
  countries: CountryRef[]
): LegalEntityPreFill {
  if (!isOdooPartnerItem(item)) return {};
  const result: LegalEntityPreFill = {};

  if (item.name) result.legal_name = item.name;
  if (typeof item.vat === 'string' && item.vat) result.tax_number = item.vat;
  if (typeof item.street === 'string' && item.street) result.address = item.street;

  const countryName = extractCountryName(item);
  const countryId = matchCountryByName(countryName, countries);
  if (countryId) {
    result.country = countryId;
  } else if (countryName) {
    result.unmatchedCountry = countryName;
  }

  return result;
}

/**
 * Map an Odoo partner item to Contact form pre-fill values.
 * The Odoo `name` field is split by the first space into first/last name.
 */
export function mapOdooToContact(
  item: OdooSearchItem,
  countries: CountryRef[]
): ContactPreFill {
  if (!isOdooPartnerItem(item)) return {};
  const result: ContactPreFill = {};

  if (item.name) {
    const spaceIndex = item.name.indexOf(' ');
    if (spaceIndex === -1) {
      result.firstName = item.name;
      result.lastName = '';
    } else {
      result.firstName = item.name.substring(0, spaceIndex);
      result.lastName = item.name.substring(spaceIndex + 1);
    }
  }

  if (typeof item.email === 'string' && item.email) result.email = item.email;

  const countryName = extractCountryName(item);
  const countryId = matchCountryByName(countryName, countries);
  if (countryId) {
    result.country = countryId;
  } else if (countryName) {
    result.unmatchedCountry = countryName;
  }

  return result;
}

/**
 * Map an Odoo product item to Plan form pre-fill values.
 */
export function mapOdooToPlan(item: OdooSearchItem): PlanPreFill {
  if (!isOdooProductItem(item)) return {};
  const result: PlanPreFill = {};

  if (item.name) result.name = item.name;
  if (typeof item.list_price === 'number') result.listPrice = item.list_price;
  if (typeof item.standard_price === 'number') result.listCost = item.standard_price;

  return result;
}

// ---------------------------------------------------------------------------
// Describe which fields will be replaced (for confirmation dialog)
// ---------------------------------------------------------------------------

/**
 * Given a pre-fill result, return a list of human-readable field names
 * that will be overwritten. Useful for the confirmation dialog.
 */
export function getPreFillFieldLabels(
  preFill: Record<string, unknown>,
  fieldLabels: Record<string, string>
): string[] {
  return Object.keys(preFill)
    .filter((key) => key !== 'unmatchedCountry' && preFill[key] != null && preFill[key] !== '')
    .map((key) => fieldLabels[key] ?? key);
}
